import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building,
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  AlertCircle,
  ArrowUpDown,
  Download,
  Users,
  BarChart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from "@/components/auth-provider";
import { Property } from "@/types/types";
import { getAllUserProperties, deleteProperty } from "@/lib/property-service";
import { formatNaira } from "@/lib/utils";
import { useEffect } from "react";

// Form schema for adding/editing properties
const propertyFormSchema = z.object({
  title: z.string().min(5, { message: "Title must be at least 5 characters" }),
  address: z.string().min(10, { message: "Please enter a complete address" }),
  type: z.string(),
  bedrooms: z.coerce.number().min(0),
  bathrooms: z.coerce.number().min(0),
  area: z.coerce.number().min(1),
  rent: z.coerce.number().min(1),
  status: z.string(),
  tenant: z.string().optional(),
  leaseEnd: z.string().optional(),
  description: z.string().optional(),
  amenities: z.array(z.string()).optional(),
});

export default function PropertiesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [properties, setProperties] = useState<Property[]>([]);
  const [_loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("title");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null
  );
  const [isAddPropertyOpen, setIsAddPropertyOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState<Property | null>(
    null
  );

  useEffect(() => {
    if (!user?.id) return;
    loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function loadProperties() {
    if (!user?.id) return;

    try {
      setLoading(true);
      console.log("Loading properties for user:", user.id, "Role:", user.role);
      // In landlord dashboard, everyone sees only their own properties
      // Even admins should see only their created properties here
      // Use getAllProperties() only in the admin dashboard for platform-wide management
      const landlordProperties = await getAllUserProperties(user.id);
      console.log(
        "Loaded properties:",
        landlordProperties.length,
        landlordProperties
      );
      setProperties(landlordProperties);
    } catch (error) {
      console.error("Error loading properties:", error);
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  }

  // Initialize form
  const form = useForm<z.infer<typeof propertyFormSchema>>({
    resolver: zodResolver(propertyFormSchema),
    defaultValues: {
      title: "",
      address: "",
      type: "apartment",
      bedrooms: 1,
      bathrooms: 1,
      area: 0,
      rent: 0,
      status: "vacant",
      tenant: "",
      leaseEnd: "",
      description: "",
      amenities: [],
    },
  });

  // Calculate statistics
  const totalProperties = properties.length;
  const occupiedProperties = properties.filter(
    (p) => p.status === "occupied"
  ).length;
  const vacantProperties = properties.filter(
    (p) => p.status === "vacant"
  ).length;
  const maintenanceProperties = properties.filter(
    (p) => p.status === "maintenance"
  ).length;
  const occupancyRate =
    totalProperties > 0
      ? Math.round((occupiedProperties / totalProperties) * 100)
      : 0;
  const totalRent = properties.reduce(
    (sum, property) => sum + property.rent,
    0
  );
  const averageRent =
    totalProperties > 0 ? Math.round(totalRent / totalProperties) : 0;

  // Filter and sort properties
  const filteredProperties = properties
    .filter((property) => {
      // Search filter
      const matchesSearch =
        searchTerm === "" ||
        property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (property.tenant &&
          property.tenant.toLowerCase().includes(searchTerm.toLowerCase()));

      // Status filter
      const matchesStatus =
        statusFilter === "all" || property.status === statusFilter;

      // Type filter
      const matchesType = typeFilter === "all" || property.type === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    })
    .sort((a, b) => {
      // Sort logic
      if (sortBy === "title") {
        return sortOrder === "asc"
          ? a.title.localeCompare(b.title)
          : b.title.localeCompare(a.title);
      } else if (sortBy === "rent") {
        return sortOrder === "asc" ? a.rent - b.rent : b.rent - a.rent;
      } else if (sortBy === "area") {
        return sortOrder === "asc" ? a.area - b.area : b.area - a.area;
      } else if (sortBy === "lastUpdated") {
        return sortOrder === "asc"
          ? new Date(a.lastUpdated).getTime() -
              new Date(b.lastUpdated).getTime()
          : new Date(b.lastUpdated).getTime() -
              new Date(a.lastUpdated).getTime();
      }
      return 0;
    });

  // Handle property selection for viewing
  // const handleViewProperty = (_property: Property) => {
  //   // navigate(`/properties/${property.id}`);
  // };

  // Handle property deletion
  const handleDeleteProperty = (property: Property) => {
    setPropertyToDelete(property);
    setIsDeleteDialogOpen(true);
  };

  // Confirm property deletion
  const confirmDeleteProperty = async () => {
    if (!propertyToDelete) return;

    try {
      await deleteProperty(propertyToDelete.id);
      toast.success("Property Deleted", {
        description: `${propertyToDelete.title} has been deleted successfully.`,
      });
      setIsDeleteDialogOpen(false);
      loadProperties(); // Reload the list
    } catch (error) {
      console.error("Error deleting property:", error);
      toast.error("Failed to delete property");
    }
    setPropertyToDelete(null);
  };

  // Handle form submission for adding/editing property
  const onSubmit = (values: z.infer<typeof propertyFormSchema>) => {
    if (selectedProperty) {
      // Edit existing property
      toast("Property Updated", {
        description: `${values.title} has been updated successfully.`,
      });
    } else {
      // Add new property
      toast("Property Added", {
        description: `${values.title} has been added to your portfolio.`,
      });
    }

    setSelectedProperty(null);
    setIsAddPropertyOpen(false);
    form.reset();
  };

  // Navigate to the property add wizard
  const handleAddPropertyClick = () => {
    navigate("/landlord/properties/add");
  };

  // Get status badge color
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "occupied":
        return <Badge className="bg-green-500">Occupied</Badge>;
      case "vacant":
        return <Badge variant="outline">Vacant</Badge>;
      case "maintenance":
        return <Badge className="bg-yellow-500">Maintenance</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Properties
            </CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalProperties}</div>
            <p className="text-xs text-muted-foreground">
              Across{" "}
              {
                Object.keys(
                  properties.reduce(
                    (acc, p) => ({ ...acc, [p.type]: true }),
                    {}
                  )
                ).length
              }{" "}
              property types
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Occupancy Rate
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{occupancyRate}%</div>
            <p className="text-xs text-muted-foreground">
              {occupiedProperties} occupied, {vacantProperties} vacant
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Rent</CardTitle>
            <div className="text-muted-foreground">₦</div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNaira(averageRent)}/mo
            </div>
            <p className="text-xs text-muted-foreground">
              {formatNaira(totalRent)}/mo total potential income
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              In Maintenance
            </CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{maintenanceProperties}</div>
            <p className="text-xs text-muted-foreground">
              Properties under maintenance
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Property Management Tools */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-[300px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search properties..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="occupied">Occupied</SelectItem>
                <SelectItem value="vacant">Vacant</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="apartment">Apartment</SelectItem>
                <SelectItem value="flat">Flat</SelectItem>
                <SelectItem value="duplex">Duplex</SelectItem>
                <SelectItem value="bungalow">Bungalow</SelectItem>
                <SelectItem value="detached">Detached</SelectItem>
                <SelectItem value="semi-detached">Semi-Detached</SelectItem>
                <SelectItem value="terrace">Terrace</SelectItem>
                <SelectItem value="penthouse">Penthouse</SelectItem>
                <SelectItem value="studio">Studio</SelectItem>
                <SelectItem value="mansion">Mansion</SelectItem>
                <SelectItem value="estate-house">Estate House</SelectItem>
                <SelectItem value="self-contain">Self-Contain</SelectItem>
                <SelectItem value="mini-flat">Mini Flat</SelectItem>
                <SelectItem value="boys-quarters">Boys Quarters</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <Download className="mr-2 h-4 w-4" /> Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Download className="mr-2 h-4 w-4" /> Export as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button size="sm" onClick={handleAddPropertyClick}>
            <Plus className="mr-2 h-4 w-4" />
            Add Property
          </Button>
        </div>
      </div>

      {/* Properties Table */}
      <Card>
        <CardHeader>
          <CardTitle>Your Properties</CardTitle>
          <CardDescription>
            Manage and monitor all your properties in one place. Showing{" "}
            {filteredProperties.length} of {properties.length} properties.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[250px]">
                  <Button
                    variant="ghost"
                    className="p-0 font-medium flex items-center"
                    onClick={() => {
                      setSortBy("title");
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    }}
                  >
                    Property
                    {sortBy === "title" && (
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    )}
                  </Button>
                </TableHead>
                <TableHead>Type</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    className="p-0 font-medium flex items-center"
                    onClick={() => {
                      setSortBy("area");
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    }}
                  >
                    Size
                    {sortBy === "area" && (
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    )}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    className="p-0 font-medium flex items-center"
                    onClick={() => {
                      setSortBy("rent");
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    }}
                  >
                    Rent
                    {sortBy === "rent" && (
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    )}
                  </Button>
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Lease End</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProperties.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No properties found matching your filters.
                  </TableCell>
                </TableRow>
              ) : (
                filteredProperties.map((property) => (
                  <TableRow key={property.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-md overflow-hidden relative">
                          <img
                            src={property.image || "/placeholder.svg"}
                            alt={property.title}
                            className="object-cover"
                          />
                        </div>
                        <div>
                          <div className="font-medium">{property.title}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                            {property.address}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">
                      {property.type.replace("-", " ")}
                    </TableCell>
                    <TableCell>{property.area} m²</TableCell>
                    <TableCell>{formatNaira(property.rent)}/mo</TableCell>
                    <TableCell>{getStatusBadge(property.status)}</TableCell>
                    <TableCell>
                      {property.tenant || (
                        <span className="text-muted-foreground text-sm">
                          None
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {property.leaseEnd || (
                        <span className="text-muted-foreground text-sm">
                          N/A
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(`/landlord/properties/${property.id}`)
                            }
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                `/landlord/properties/${property.id}/edit`
                              )
                            }
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit Property
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => handleDeleteProperty(property)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Property
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
        <CardFooter className="flex justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {filteredProperties.length} of {properties.length}{" "}
            properties
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled>
              Next
            </Button>
          </div>
        </CardFooter>
      </Card>

      {/* Property Analytics */}
      <Card>
        <CardHeader>
          <CardTitle>Property Analytics</CardTitle>
          <CardDescription>
            Key metrics and performance indicators for your property portfolio.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="occupancy">
            <TabsList className="mb-4">
              <TabsTrigger value="occupancy">Occupancy</TabsTrigger>
              <TabsTrigger value="revenue">Revenue</TabsTrigger>
              <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
            </TabsList>
            <TabsContent value="occupancy" className="space-y-4">
              <div className="h-[300px] bg-muted/20 rounded-md flex items-center justify-center">
                <div className="text-center">
                  <BarChart className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-muted-foreground">Occupancy Rate Chart</p>
                  <p className="text-sm text-muted-foreground">
                    (In a real app, this would be an interactive chart showing
                    occupancy trends)
                  </p>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="revenue" className="space-y-4">
              <div className="h-[300px] bg-muted/20 rounded-md flex items-center justify-center">
                <div className="text-center">
                  <BarChart className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-muted-foreground">Revenue Chart</p>
                  <p className="text-sm text-muted-foreground">
                    (In a real app, this would be an interactive chart showing
                    revenue trends)
                  </p>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="maintenance" className="space-y-4">
              <div className="h-[300px] bg-muted/20 rounded-md flex items-center justify-center">
                <div className="text-center">
                  <BarChart className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    Maintenance Issues Chart
                  </p>
                  <p className="text-sm text-muted-foreground">
                    (In a real app, this would be an interactive chart showing
                    maintenance trends)
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Add/Edit Property Dialog */}
      <Dialog
        open={isAddPropertyOpen || selectedProperty !== null}
        onOpenChange={(open) => {
          if (!open) {
            setIsAddPropertyOpen(false);
            setSelectedProperty(null);
          }
        }}
      >
        <DialogContent className="overflow-auto max-h-[90vh] w-full max-w-[90vw]">
          <DialogHeader>
            <DialogTitle>
              {selectedProperty ? "Edit Property" : "Add New Property"}
            </DialogTitle>
            <DialogDescription>
              {selectedProperty
                ? "Update the details of your property."
                : "Fill in the details to add a new property to your portfolio."}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Property Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter property title" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Property Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select property type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="apartment">Apartment</SelectItem>
                          <SelectItem value="house">House</SelectItem>
                          <SelectItem value="studio">Studio</SelectItem>
                          <SelectItem value="townhouse">Townhouse</SelectItem>
                          <SelectItem value="penthouse">Penthouse</SelectItem>
                          <SelectItem value="loft">Loft</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter full property address"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <FormField
                  control={form.control}
                  name="bedrooms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bedrooms</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bathrooms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bathrooms</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" step="0.5" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="area"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Area (m²)</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="rent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Monthly Rent (₦)</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="vacant">Vacant</SelectItem>
                          <SelectItem value="occupied">Occupied</SelectItem>
                          <SelectItem value="maintenance">
                            Maintenance
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="tenant"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Current Tenant</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Tenant name (if occupied)"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Leave blank if property is vacant
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="leaseEnd"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lease End Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormDescription>
                        Leave blank if property is vacant
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Property Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter a detailed description of the property"
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="amenities"
                render={() => (
                  <FormItem>
                    <div className="mb-4">
                      <FormLabel>Amenities</FormLabel>
                      <FormDescription>
                        Select all amenities available at this property
                      </FormDescription>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        "Parking",
                        "Gym",
                        "Pool",
                        "Elevator",
                        "Balcony",
                        "Laundry",
                        "Dishwasher",
                        "Air Conditioning",
                        "Heating",
                        "Furnished",
                      ].map((amenity) => (
                        <FormField
                          key={amenity}
                          control={form.control}
                          name="amenities"
                          render={({ field }) => {
                            return (
                              <FormItem
                                key={amenity}
                                className="flex flex-row items-start space-x-3 space-y-0"
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(amenity)}
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([
                                            ...(field.value || []),
                                            amenity,
                                          ])
                                        : field.onChange(
                                            field.value?.filter(
                                              (value) => value !== amenity
                                            )
                                          );
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {amenity}
                                </FormLabel>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="submit">
                  {selectedProperty ? "Update Property" : "Add Property"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;
              {propertyToDelete?.title ?? ""}&quot;? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteProperty}>
              Delete Property
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
